import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Share2, Megaphone, Search, Code, Target, GitBranch } from 'lucide-react'
import { trackEvent } from '../analytics'
import type { LucideIcon } from 'lucide-react'
import { servicePagesContent } from '../data/servicePages'

const iconMap: Record<string, LucideIcon> = {
  'social-media-marketing': Share2,
  'paid-ads': Megaphone,
  'seo': Search,
  'website-development': Code,
  'lead-generation': Target,
  'funnel-building': GitBranch,
}

export default function ServicesPage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="min-h-screen"
    >
      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 dark:from-primary/10 dark:to-transparent" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary/10 dark:bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/10 dark:bg-primary/10 rounded-full blur-3xl" />
        
        {/* Abstract CSS Blob */}
        <div className="absolute top-20 right-10 w-64 h-64 opacity-30 dark:opacity-20 pointer-events-none select-none">
          <div className="w-full h-full bg-gradient-to-br from-primary to-accent rounded-[30%_70%_70%_30%/30%_30%_70%_70%] animate-[spin_20s_linear_infinite] blur-xl mix-blend-multiply dark:mix-blend-screen" />
        </div>
        <div className="container-narrow relative">
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-accent font-semibold text-sm uppercase tracking-wider"
          >
            Our Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary dark:text-white mt-2 mb-6 max-w-3xl"
          >
            Systems Built For Business Growth
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl leading-relaxed"
          >
            We don't just execute marketing tasks. We help businesses build structured digital systems that consistently generate opportunities and revenue.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="container-wide">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {servicePagesContent.map((service, i) => {
              const Icon = iconMap[service.slug] || Target
              return (
                <motion.div
                  key={service.slug}
                  className="group relative card-glass rounded-card p-6 sm:p-8 overflow-hidden flex flex-col"
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  whileHover={{ y: -8, transition: { duration: 0.25 } }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 dark:from-primary/10 dark:to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex-1 flex flex-col">
                    <motion.div
                      className="w-14 h-14 rounded-2xl bg-primary/10 dark:bg-accent/10 flex items-center justify-center mb-6"
                      whileHover={{ scale: 1.08, rotate: 3 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <Icon className="text-primary dark:text-accent" size={28} strokeWidth={1.8} />
                    </motion.div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">{service.title}</h2>
                    <p className="mt-2 text-accent font-medium text-sm">{service.tagline}</p>
                    <p className="mt-4 text-slate-600 dark:text-slate-400 text-sm sm:text-base leading-relaxed line-clamp-3 flex-1">
                      {service.intro}
                    </p>
                    <Link
                      to={`/services/${service.slug}`}
                      className="mt-8 inline-flex items-center gap-2 text-primary dark:text-accent font-medium text-sm group/link w-fit"
                    >
                      Explore Service
                      <motion.span
                        className="inline-block"
                        initial={false}
                        whileHover={{ x: 4 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                      >
                        <ArrowRight size={16} strokeWidth={2} />
                      </motion.span>
                    </Link>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50/50 dark:bg-slate-900/30">
        <div className="container-narrow text-center">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-primary dark:text-white mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to scale your business?
          </motion.h2>
          <motion.p
            className="text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Let's discuss how our structured growth systems can help you generate predictable revenue.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <Link to="/contact" className="btn-primary" onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'services_page' })}>
              Book Strategy Call
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  )
}
